import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, AlertTriangle, ArrowLeft, ArrowRight, Loader2, CheckCircle2, History, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, EXP_STATUS } from "../lib/api";
import { getP, setP } from "../lib/casePath";
import { Btn, Card, FieldGrid, Pill } from "../components/kit/Kit";
import { clienteFields, REVISION_GROUPS, creditoFields, buroFields, garFields, proyFields, missingIn } from "../components/wizard/fields";
import { StepDocumentos } from "../components/wizard/StepDocumentos";
import { StepRevision } from "../components/wizard/StepRevision";
import { StepCredito } from "../components/wizard/StepCredito";
import { StepAnalisis } from "../components/wizard/StepAnalisis";
import { Garantias } from "../components/riesgo/Garantias";
import { StepResumen } from "../components/wizard/StepResumen";
import { StepDictamen, dictamenCompleto } from "../components/wizard/StepDictamen";
import { Historial } from "../components/wizard/Historial";
import { ReviewPanel, StatusBanner } from "../components/wizard/ReviewPanel";
import { esAnalista, useAuth } from "../lib/auth";

const STEPS = ["Cliente", "Documentos", "Revisión de datos", "Crédito", "Buró y garantías", "Análisis", "Dictamen", "Resumen y envío"];

export default function Wizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("exp");
  const [exp, setExp] = useState(null);
  const [cat, setCat] = useState(null);
  const [step, setStep] = useState(() => Math.min(7, Math.max(0, Number(new URLSearchParams(window.location.search).get("paso") || 1) - 1)));
  const [jobs, setJobs] = useState([]);
  const [result, setResult] = useState(null);
  const [save, setSave] = useState("saved");
  const dirty = useRef(false);
  const dDirty = useRef(false);

  useEffect(() => {
    http.get(`/expedientes/${id}`).then((r) => setExp(r.data)).catch(() => { toast.error("Expediente no encontrado"); nav("/"); });
    http.get("/motor/params").then((r) => setCat(r.data));
    http.get(`/expedientes/${id}/jobs`).then((r) => setJobs(r.data)).catch(() => null);
    http.post("/notifications/read", { expediente_id: id }).catch(() => null);
  }, [id, nav]);

  const roDocs = exp && exp.status !== "borrador";
  const ro = roDocs || user.role === "comercial";
  const onChange = useCallback((path, v) => {
    if (ro) return;
    dirty.current = true;
    setSave("pending");
    setExp((e) => ({ ...e, case: setP(e.case, path, v), edited: e.sources?.[path] && !e.edited.includes(path) ? [...e.edited, path] : e.edited }));
  }, [ro]);
  const onDictamen = useCallback((k, v) => {
    if (ro) return;
    dirty.current = true;
    dDirty.current = true;
    setSave("pending");
    setExp((e) => ({ ...e, dictamen: { ...e.dictamen, secciones: { ...e.dictamen.secciones, [k]: v },
      editadas: e.dictamen.editadas.includes(k) ? e.dictamen.editadas : [...e.dictamen.editadas, k] } }));
  }, [ro]);

  useEffect(() => {
    if (!exp || !dirty.current) return undefined;
    const t = setTimeout(async () => {
      setSave("saving");
      const withD = dDirty.current;
      try {
        await http.patch(`/expedientes/${id}`, { case: exp.case, edited: exp.edited,
          ...(withD ? { dictamen: { secciones: exp.dictamen.secciones, editadas: exp.dictamen.editadas } } : {}) });
        dirty.current = false;
        if (withD) dDirty.current = false;
        setSave("saved");
      } catch (e) { setSave("error"); toast.error(errMsg(e, "No se pudo guardar")); }
    }, 900);
    return () => clearTimeout(t);
  }, [exp, id]);

  useEffect(() => {
    if (!exp || !esAnalista(user)) return undefined;
    const t = setTimeout(() => {
      http.post("/motor/evaluate", { case: exp.case, params: { comision: exp.case.cred.comision || 2 } }).then((r) => setResult(r.data)).catch(() => null);
    }, 500);
    return () => clearTimeout(t);
  }, [exp?.case]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!exp || !cat) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="animate-spin text-[#E8643C]" /></div>;
  const c = exp.case;
  const revFields = REVISION_GROUPS.flatMap((g) => g[1]);
  const b = c.buro;
  const state = [
    missingIn(clienteFields(cat), c).length === 0,
    jobs.some((j) => j.status === "done") && !jobs.some((j) => j.status === "error"),
    missingIn(revFields, c).length === 0,
    missingIn(creditoFields, c).length === 0,
    b.consultado && b.kyc !== "pend" && b.fraude !== "pend" && b.fiscal !== "pend",
    result && result.rec !== "Rechazar",
    dictamenCompleto(exp.dictamen),
    exp.status !== "borrador",
  ];
  const ctx = { exp, setExp, c, onChange, onDictamen, cat, result, jobs, setJobs, ro, id, state, STEPS, goTo: setStep };
  const isDirector = user?.role === "director";

  return (
    <main className="mx-auto max-w-7xl px-6 pb-32 pt-8 lg:px-10" data-testid="wizard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <button onClick={() => nav(`/deals/${id}`)} className="text-sm text-[#8C867A] hover:text-[#E8643C]" data-testid="wizard-back-to-list">← Ficha del deal</button>
          <h1 className="font-serif-display mt-1 truncate text-3xl font-bold sm:text-4xl" data-testid="wizard-title">{exp.nombre}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-[#111] px-2.5 py-0.5 text-xs font-bold text-white" data-testid="wizard-version">v{exp.version}</span>
          <Pill cls={EXP_STATUS[exp.status][1]} testid="wizard-status">{EXP_STATUS[exp.status][0]}</Pill>
          <span className="flex items-center gap-1.5 text-sm text-[#5C5850]" data-testid="autosave-indicator">
            {save === "saved" && <><CheckCircle2 size={16} className="text-emerald-600" /> Guardado</>}
            {(save === "saving" || save === "pending") && <><Loader2 size={15} className="animate-spin" /> Guardando…</>}
            {save === "error" && <span className="text-rose-600">Error al guardar</span>}
          </span>
        </div>
      </div>

      <div className="mt-6 inline-flex gap-1 rounded-xl border border-[#E2DDD5] bg-white p-1" data-testid="wizard-tabs">
        {[["exp", "Expediente", <ClipboardList key="i" size={15} />], ["hist", "Historial", <History key="i" size={15} />]].map(([k, l, ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-[#111] text-white" : "text-[#5C5850] hover:bg-[#F2F0EC]"}`}>{ic}{l}</button>
        ))}
      </div>

      {exp.status === "en_revision" && isDirector ? <ReviewPanel id={id} exp={exp} setExp={setExp} /> : <StatusBanner id={id} exp={exp} setExp={setExp} isDirector={isDirector} />}

      {tab === "hist" ? <div className="mt-8"><Historial id={id} exp={exp} /></div> : <>
      <nav className="sticky top-[72px] z-30 mt-6 rounded-2xl border border-[#E2DDD5] bg-white/90 p-3 shadow-sm backdrop-blur-md" data-testid="wizard-stepper">
        <div className="mb-3 h-1 overflow-hidden rounded-full bg-[#F2F0EC]"><div className="h-full rounded-full bg-[#E8643C] transition-all duration-500" style={{ width: `${(state.filter(Boolean).length / STEPS.length) * 100}%` }} /></div>
        <ol className="grid grid-cols-8 gap-1">
          {STEPS.map((s, i) => (
            <li key={s}>
              <button onClick={() => setStep(i)} data-testid={`wizard-step-${i + 1}`}
                className={`flex w-full flex-col items-center gap-1.5 rounded-xl px-1 py-2 text-center transition-colors ${i === step ? "bg-[#FDF0EB]" : "hover:bg-[#FAF8F5]"}`}>
                <span className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition-all ${i === step ? "bg-[#E8643C] text-white ring-4 ring-[#E8643C]/20" : state[i] ? "bg-emerald-600 text-white" : "bg-[#F2F0EC] text-[#5C5850]"}`}>
                  {state[i] && i !== step ? <Check size={15} /> : i + 1}
                </span>
                <span className={`hidden text-xs sm:block ${i === step ? "font-semibold" : "text-[#5C5850]"}`}>{s}</span>
                {!state[i] && i !== 7 && <AlertTriangle size={12} className="text-amber-500" data-testid={`wizard-step-${i + 1}-warn`} />}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-8 space-y-8" key={step} style={{ animation: "rise .35s ease-out both" }}>
        {step === 0 && <Card title="Datos del solicitante" subtitle="Campos del motor (sol.*). Persona moral o física con actividad empresarial." testid="step-cliente"><FieldGrid fields={clienteFields(cat)} data={c} onChange={onChange} /></Card>}
        {step === 1 && <StepDocumentos {...ctx} ro={roDocs} />}
        {step === 2 && <StepRevision {...ctx} />}
        {step === 3 && <StepCredito {...ctx} />}
        {step === 4 && (
          <>
            <Card title="Buró, KYC y cumplimiento" testid="step-buro"><FieldGrid fields={buroFields} data={c} onChange={onChange} /></Card>
            <Card title="Afectación de flujos y reciprocidad" testid="step-garantias"><FieldGrid fields={garFields} data={c} onChange={onChange} /></Card>
            <Garantias id={ctx.id} ro={ctx.ro} />
            <Card title="Proyección y supuestos" subtitle="Base del DSCR del motor." testid="step-proyeccion"><FieldGrid fields={proyFields} data={c} onChange={onChange} /></Card>
          </>
        )}
        {step === 5 && <StepAnalisis {...ctx} />}
        {step === 6 && <StepDictamen {...ctx} />}
        {step === 7 && <StepResumen {...ctx} />}
      </div>
      </>}

      {tab === "exp" && <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E2DDD5] bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
          <Btn kind="secondary" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev-button"><ArrowLeft size={16} /> Anterior</Btn>
          <span className="text-sm text-[#8C867A]">Paso {step + 1} de {STEPS.length} · {getP(c, "sol.razon") || "Sin nombre"}</span>
          <Btn kind="primary" disabled={step === STEPS.length - 1} onClick={() => setStep(step + 1)} data-testid="wizard-next-button">Siguiente <ArrowRight size={16} /></Btn>
        </div>
      </div>}
    </main>
  );
}
